/**
 * PipelineNewVersionDialog — Two-step flow for creating a new pipeline version.
 *
 * Step 1: Source Mapping Confirmation
 *   Calls prepare-new-version → shows suggested source table mappings.
 *   User confirms or edits each mapping via dropdowns.
 *
 * Step 2: Drift Preview
 *   Calls new-version with confirmed mapping → shows drift summary.
 *   User clicks "Open Pipeline" to navigate to the cloned pipeline.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { PrepareNewVersionResult, NewVersionResult } from '../../services/workspaceApi';

interface PipelineNewVersionDialogProps {
  open: boolean;
  onClose: () => void;
  onPrepare: (sourcePipelineId?: string) => Promise<PrepareNewVersionResult>;
  onCreate: (
    sourceTableMapping: Record<string, string>,
    sourcePipelineId?: string,
  ) => Promise<NewVersionResult>;
  preparing?: boolean;
  creating?: boolean;
}

const PipelineNewVersionDialog: React.FC<PipelineNewVersionDialogProps> = ({
  open,
  onClose,
  onPrepare,
  onCreate,
  preparing,
  creating,
}) => {
  const [step, setStep] = useState<'mapping' | 'drift'>('mapping');
  const [prepareResult, setPrepareResult] = useState<PrepareNewVersionResult | null>(null);
  const [versionResult, setVersionResult] = useState<NewVersionResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const onPrepareRef = useRef(onPrepare);
  onPrepareRef.current = onPrepare;

  // Fetch suggestions when dialog opens
  useEffect(() => {
    if (!open) {
      setStep('mapping');
      setPrepareResult(null);
      setVersionResult(null);
      setMapping({});
      setError(null);
      return;
    }

    (async () => {
      try {
        const result = await onPrepareRef.current();
        setPrepareResult(result);
        // Initialize mapping from suggestions
        const initial: Record<string, string> = {};
        for (const [oldTable, suggestion] of Object.entries(result.suggested_mapping)) {
          initial[oldTable] = suggestion.new_table;
        }
        setMapping(initial);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to prepare new version');
      }
    })();
  }, [open]);

  const handleMappingChange = useCallback((oldTable: string, newTable: string) => {
    setMapping((prev) => ({ ...prev, [oldTable]: newTable }));
  }, []);

  const unmappedCount = prepareResult
    ? [...Object.keys(prepareResult.suggested_mapping), ...prepareResult.unmapped_sources]
        .filter((src) => !mapping[src]).length
    : 0;

  const handleCreate = useCallback(async () => {
    setError(null);
    try {
      // Only send entries that have a mapped value — unmapped sources are
      // intentionally skipped and will appear as broken nodes in the DAG.
      const cleanMapping = Object.fromEntries(
        Object.entries(mapping).filter(([, v]) => !!v),
      );
      const result = await onCreate(cleanMapping, prepareResult?.parent_pipeline_id);
      setVersionResult(result);
      setStep('drift');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create new version');
    }
  }, [mapping, prepareResult, onCreate]);

  const allSources = prepareResult
    ? [...Object.keys(prepareResult.suggested_mapping), ...prepareResult.unmapped_sources]
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {step === 'mapping' ? 'New Pipeline Version — Confirm Sources' : 'New Pipeline Version — Drift Summary'}
      </DialogTitle>
      <DialogContent>
        {step === 'mapping' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Map each source table from the parent pipeline to a table in the current workspace.
              Tables with matching names are pre-selected.
            </Typography>

            {preparing && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {prepareResult?.removed_sources && prepareResult.removed_sources.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
                {prepareResult.removed_sources.length} source connector(s) were removed since the
                parent pipeline:{' '}
                {prepareResult.removed_sources.map((s) => s.label || s.source_id).join(', ')}.
                Tables from these connectors will need to be re-mapped.
              </Alert>
            )}

            {prepareResult && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Parent Source</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>New Source</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Match</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allSources.map((oldTable) => {
                      const suggestion = prepareResult.suggested_mapping[oldTable];
                      const isUnmapped = !suggestion;

                      return (
                        <TableRow key={oldTable}>
                          <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                            {oldTable}
                          </TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              value={mapping[oldTable] || ''}
                              onChange={(e) => handleMappingChange(oldTable, e.target.value)}
                              displayEmpty
                              sx={{
                                fontSize: 12,
                                minWidth: 180,
                                fontFamily: 'monospace',
                                ...(isUnmapped && !mapping[oldTable] ? { border: '1px solid #ef4444' } : {}),
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>Select table…</em>
                              </MenuItem>
                              {(() => {
                                // Group tables by connector origin for easier selection
                                const groups = prepareResult.connector_groups || {};
                                const groupedTables = new Set(
                                  Object.values(groups).flat(),
                                );
                                const ungrouped = prepareResult.new_tables_available.filter(
                                  (t) => !groupedTables.has(t),
                                );
                                const items: React.ReactNode[] = [];

                                // Render grouped tables with subheaders
                                for (const [groupLabel, tables] of Object.entries(groups)) {
                                  items.push(
                                    <ListSubheader
                                      key={`group-${groupLabel}`}
                                      sx={{ fontSize: 11, fontWeight: 700, lineHeight: '28px' }}
                                    >
                                      {groupLabel}
                                    </ListSubheader>,
                                  );
                                  for (const t of [...tables].sort()) {
                                    const sepIdx = t.indexOf('::');
                                    const displayName = sepIdx > 0 ? t.substring(sepIdx + 2) : t;
                                    items.push(
                                      <MenuItem
                                        key={t}
                                        value={t}
                                        sx={{ fontSize: 12, fontFamily: 'monospace', pl: 3 }}
                                      >
                                        {displayName}
                                      </MenuItem>,
                                    );
                                  }
                                }

                                // Render ungrouped tables (file uploads, etc.)
                                if (ungrouped.length > 0) {
                                  if (Object.keys(groups).length > 0) {
                                    items.push(
                                      <ListSubheader
                                        key="group-other"
                                        sx={{ fontSize: 11, fontWeight: 700, lineHeight: '28px' }}
                                      >
                                        Other
                                      </ListSubheader>,
                                    );
                                  }
                                  for (const t of ungrouped) {
                                    items.push(
                                      <MenuItem
                                        key={t}
                                        value={t}
                                        sx={{ fontSize: 12, fontFamily: 'monospace' }}
                                      >
                                        {t}
                                      </MenuItem>,
                                    );
                                  }
                                }

                                return items;
                              })()}
                            </Select>
                          </TableCell>
                          <TableCell>
                            {mapping[oldTable] && suggestion ? (
                              <Chip
                                label={suggestion.match_type === 'exact' ? 'Exact' : `${Math.round(suggestion.confidence * 100)}%`}
                                size="small"
                                color={suggestion.confidence >= 0.9 ? 'success' : 'warning'}
                                variant="outlined"
                                sx={{ fontSize: 10, height: 20 }}
                              />
                            ) : mapping[oldTable] ? (
                              <Chip
                                label="Manual"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: 10, height: 20 }}
                              />
                            ) : prepareResult.sources_from_removed_connectors?.includes(oldTable) ? (
                              <Chip
                                label="Removed"
                                size="small"
                                color="error"
                                variant="filled"
                                sx={{ fontSize: 10, height: 20 }}
                              />
                            ) : (
                              <Chip
                                label="Skipped"
                                size="small"
                                sx={{ fontSize: 10, height: 20, color: '#6b7280', borderColor: '#d1d5db' }}
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {prepareResult && unmappedCount > 0 && (
              <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                {unmappedCount} source(s) have no mapping and will appear as broken nodes in the pipeline.
                You can still create the version and fix them later.
              </Alert>
            )}
          </>
        )}

        {step === 'drift' && versionResult && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pipeline version {versionResult.version} created with {versionResult.steps_cloned} steps.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {versionResult.drift_report.summary.green > 0 && (
                <Chip
                  label={`${versionResult.drift_report.summary.green} unchanged`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {versionResult.drift_report.summary.amber > 0 && (
                <Chip
                  label={`${versionResult.drift_report.summary.amber} stale`}
                  size="small"
                  color="warning"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {versionResult.drift_report.summary.red > 0 && (
                <Chip
                  label={`${versionResult.drift_report.summary.red} broken`}
                  size="small"
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>

            {versionResult.drift_report.summary.red > 0 && (
              <Alert severity="warning" sx={{ mb: 1, fontSize: 12 }}>
                {versionResult.drift_report.summary.red} step(s) are broken — missing source
                tables or incompatible schema changes. Open the pipeline to review and fix
                the highlighted nodes.
              </Alert>
            )}

            {versionResult.drift_report.summary.red === 0 && versionResult.drift_report.summary.amber === 0 && (
              <Alert severity="success" sx={{ mb: 1, fontSize: 12 }}>
                All steps are compatible with the new source data. The pipeline is ready to execute.
              </Alert>
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          {step === 'drift' ? 'Close' : 'Cancel'}
        </Button>
        {step === 'mapping' && (
          <Button
            onClick={handleCreate}
            disabled={creating || !prepareResult}
            variant="contained"
            color="primary"
          >
            {creating ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Create Version
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PipelineNewVersionDialog;
